import { BrowserModule } from '@angular/platform-browser';
import { NgModule, LOCALE_ID } from '@angular/core';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeId from '@angular/common/locales/id';

import { AppComponent } from './app.component';

import { LoginModule } from './views/login/login.module';

// Import containers
import { FullLayoutComponent, SimpleLayoutComponent, CashierLayoutComponent } from './containers';

const APP_CONTAINERS = [FullLayoutComponent, SimpleLayoutComponent, CashierLayoutComponent];

// Import components
import {
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppCashierPeriodeComponent,
  AppFooterComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
  AppSidebarMinimizerComponent,
  APP_SIDEBAR_NAV,
} from './components';

const APP_COMPONENTS = [
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppCashierPeriodeComponent,
  AppFooterComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
  AppSidebarMinimizerComponent,
  APP_SIDEBAR_NAV,
];

// Import directives
import {
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  ReplaceDirective,
  SIDEBAR_TOGGLE_DIRECTIVES,
} from './directives';

const APP_DIRECTIVES = [
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  ReplaceDirective,
  SIDEBAR_TOGGLE_DIRECTIVES,
];

// Import routing module
import { AppRoutingModule } from './app.routing';

// Import 3rd party components
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { ToastrModule } from 'ngx-toastr';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { jwtOptionsFactory } from './class/jwtoptions';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';

// Import app services
import { AuthService } from './services/auth/auth.service';
import { CourseService } from './services/course/course.service';
import { PeriodeService } from './services/periode/periode.service';
import { RegistrationService } from './services/registration/registration.service';
import { TemplateService } from './services/template/template.service';
import { UserService } from './services/user/user.service';
import { ErrorhandlerService } from './services/errorhandler/errorhandler.service';
import { DateConvertService } from './services/date-convert/date-convert.service';
import { StudentService } from './services/student/student.service';
import { ManualPaymentService } from './services/manual-payment/manual-payment.service';
import { AddressService } from './services/address/address.service';
import { PaymentService } from './services/payment/payment.service';
import { AutoPaymentService } from './services/auto-payment/auto-payment.service';
import { NotificationService } from './services/notification/notification.service';
import { IdnPaymentService } from './services/idn-payment/idn-payment.service';
import { ReportService } from './services/report/report.service';
import { LeadsService } from './services/leads/leads.service';
import { AuthInterceptor } from './auth.interceptor';
import { environment } from '../environments/environment';

registerLocaleData(localeId);

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    LoginModule,
    HttpClientModule,
    NgProgressModule,
    NgProgressHttpModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      enableHtml: true,
      progressBar: true,
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
      },
    }),
    NgbModule,
  ],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    ...APP_COMPONENTS,
    ...APP_DIRECTIVES,
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    {
      provide: LOCALE_ID,
      useValue: 'id-id',
    },
    AuthService,
    CourseService,
    ErrorhandlerService,
    PeriodeService,
    RegistrationService,
    TemplateService,
    UserService,
    DateConvertService,
    StudentService,
    PaymentService,
    AutoPaymentService,
    ManualPaymentService,
    AddressService,
    NotificationService,
    IdnPaymentService,
    ReportService,
    LeadsService,
    ...(environment.production
      ? []
      : [
          {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
          },
        ]),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
